/**
 * In-memory canned replies used when live inbox mode is off.
 */
import { INITIAL_CANNED_RESPONSES } from "../mocks/cannedResponses.js";
import { normalizeShortcut } from "../lib/cannedReplyUtils.js";

export class CannedResponsesSkeletonStore {
  /**
   * @param {Array<{ id: string, shortcut: string, title: string, body: string, visibility?: string }>} [seed]
   */
  constructor(seed = INITIAL_CANNED_RESPONSES) {
    this._rows = seed.map((row) => this._clone(row));
  }

  listMine() {
    return this._rows.map((row) => this._clone(row));
  }

  /**
   * @param {{ id: string, shortcut?: string, title?: string, body?: string, visibility?: string }} patch
   */
  update(patch) {
    const id = String(patch?.id || "").trim();
    const index = this._rows.findIndex((row) => row.id === id);
    if (index < 0) {
      throw new Error("Saved reply not found.");
    }
    const current = this._rows[index];
    let shortcut = current.shortcut;
    if (patch.shortcut !== undefined) {
      shortcut = normalizeShortcut(patch.shortcut);
      if (!shortcut) throw new Error("Invalid shortcut.");
      if (this._rows.some((row) => row.id !== id && row.shortcut === shortcut)) {
        throw new Error(`Shortcut /${shortcut} is already used.`);
      }
    }
    const title = patch.title !== undefined ? String(patch.title || "").trim() : current.title;
    const body = patch.body !== undefined ? String(patch.body || "").trim() : current.body;
    if (!title) throw new Error("Title is required.");
    if (!body) throw new Error("Body is required.");
    const visibility = ["public", "pvt", "workspace_wide"].includes(patch.visibility)
      ? patch.visibility
      : current.visibility || "public";
    const next = { ...current, shortcut, title, body, visibility };
    this._rows = [...this._rows.slice(0, index), next, ...this._rows.slice(index + 1)];
    return this._clone(next);
  }

  /**
   * @param {string} id
   */
  delete(id) {
    const target = String(id || "").trim();
    const before = this._rows.length;
    this._rows = this._rows.filter((row) => row.id !== target);
    if (this._rows.length === before) {
      throw new Error("Saved reply not found.");
    }
  }

  reset(seed = INITIAL_CANNED_RESPONSES) {
    this._rows = seed.map((row) => this._clone(row));
  }

  _clone(row) {
    return {
      id: String(row.id),
      shortcut: String(row.shortcut || ""),
      title: String(row.title || ""),
      body: String(row.body || ""),
      visibility: ["public", "pvt", "workspace_wide"].includes(row.visibility)
        ? row.visibility
        : "public",
      human_agent_id: row.human_agent_id ?? null,
      created_by: row.created_by ?? null,
      created_at: row.created_at ?? null,
    };
  }
}

export const cannedResponsesSkeletonStore = new CannedResponsesSkeletonStore();
